/**
 * POM-6537 — which other rows of the review one picked lot can be applied to, and what each would
 * be staged as.
 *
 * Offered for the other rows with the edited row's NDC, whatever else the batch holds: a row of
 * another NDC is never listed, so a lot never crosses NDCs. Only the rows that can take the
 * lot are listed: a row already on it, one with an edit of its own pending, and a dispensed or
 * locked row are left out rather than explained, so the operator sees only what they can act on.
 * A row left out because its facility does not stock the lot is counted per facility instead
 * (POM-6506 AC 4), since the dialog names only the edited row's facility.
 *
 * Pure: no React, no network.
 */

import { applyBatchLotUsage } from './batchLotStatus';
import { lotBalancesInBatch, settleBatchClaims } from './batchLotUsage';
import { REVIEW_GROUP, isDispensedOrCountLocked, reviewGroupFor } from './bulkLotNdcReviewGroups';
import {
  buildStockLotSelection,
  isClaimOnRecordedLot,
  isManualLotSelection,
  stageLotNdcChange,
} from './bulkLotNdcStaging';
import { effectiveNdcFor, isLotSelectableForFill, toDateOnly } from './bulkLotNdcStatus';
import { buildManualLotSelection, isRecordableNdc, isSameLotNumber } from './bulkManualLotEntry';
import { ndcDigits } from './bulkOverrideFilters';

export const BULK_APPLY_SCOPE = {
  /** The default: only the rows waiting on a decision. */
  NEEDS_ATTENTION: 'NEEDS_ATTENTION',
  /** Also the rows whose lot is fine. */
  ALL_ROWS: 'ALL_ROWS',
};

/** Why there is nothing to offer for this pick; Change NDC & lot then leaves "Also apply" out. */
export const BULK_APPLY_UNAVAILABLE = {
  NO_LOT_PICKED: 'NO_LOT_PICKED',
  OTHER_NDC_PICKED: 'OTHER_NDC_PICKED',
};

/** Recorded on each bulk row's change, so the audit can tell it from a row edited on its own. */
export const BULK_APPLY_CHANGE_SOURCE = 'BULK_APPLY';

const isSameNdc = (left, right) => !!left && ndcDigits(left) === ndcDigits(right);

/** Does the row print this NDC, counting a change staged on it? */
const isRowOnNdc = ({ row, changes, ndc }) =>
  isSameNdc(
    effectiveNdcFor({ item: row.item, change: changes?.[row.item.requestLineItemId] }),
    ndc
  );

/** The availability or recordable-lots entry for the item's product and facility. */
export const entryForProductAndFacility = (entries, item) =>
  (entries ?? []).find(
    (entry) =>
      entry.productId === item.productId && entry.fulfillingPharmacyId === item.fulfillingPharmacyId
  );

const isInNeedsAttention = (row) => reviewGroupFor(row.status) === REVIEW_GROUP.NEEDS_ATTENTION;

const isInScope = (row, scope) => {
  if (isDispensedOrCountLocked(row.status)) return false;
  if (isInNeedsAttention(row)) return true;

  return (
    scope === BULK_APPLY_SCOPE.ALL_ROWS &&
    reviewGroupFor(row.status) === REVIEW_GROUP.NO_ACTION_NEEDED
  );
};

/** Could a lot be given to this row in bulk, were its NDC picked? */
export const canTakeBulkLot = (row) => isInScope(row, BULK_APPLY_SCOPE.ALL_ROWS);

const mayClaimShortfall = (item, isManualLotEnabled) =>
  isManualLotEnabled === true && item.canRecordUnverifiedLot === true;

const soleRecordedLot = (item) =>
  (item.recordedLots ?? []).length === 1 ? item.recordedLots[0] : null;

/**
 * The batch as it would stand once the edited row's pick is applied: its change or claim staged,
 * and every other row weighed again. A row that was short only because the edited row sat ahead
 * of it on a lot needs nothing once that row moves off, so it must not be listed as needing a lot.
 *
 * "Bulk lot update" edits no row of its own, so there the batch is weighed as it stands.
 */
const batchWithPick = ({
  classified,
  items,
  availability,
  changes,
  claims,
  editedItem,
  selection,
  isManualLotEnabled,
}) => {
  const { requestLineItemId } = editedItem;
  const { ndc, lotAllocations } = selection;
  const changesWithPick = requestLineItemId
    ? stageLotNdcChange({ changes, item: editedItem, requestLineItemId, ndc, lotAllocations })
    : changes;
  const claimsWithPick = Object.fromEntries(
    Object.entries(claims ?? {}).filter(([id]) => id !== requestLineItemId)
  );
  if (requestLineItemId && isClaimOnRecordedLot({ item: editedItem, ndc, lotAllocations })) {
    claimsWithPick[requestLineItemId] = lotAllocations[0].inventoryLotId;
  }

  const { usage, activeClaims } = settleBatchClaims({
    items,
    availability,
    changes: changesWithPick,
    claims: claimsWithPick,
  });
  const review = applyBatchLotUsage({
    classified,
    usage,
    claims: activeClaims,
    isManualLotEnabled,
  });

  return { rows: [...review.rows, ...review.unlistedRows], changes: changesWithPick };
};

const isAtEditedFacility = (item, editedItem) =>
  item.fulfillingPharmacyId === editedItem.fulfillingPharmacyId;

/**
 * The same lot at this row's own facility: the very record that was picked at the edited row's
 * facility (one facility can hold two records under one lot number), and the record with the same
 * lot number and BUD at any other. Null when the facility does not stock it.
 */
const findPickedLotAtRowFacility = ({ item, editedItem, targetLot, ndc, availability }) =>
  (entryForProductAndFacility(availability, item)?.groups ?? [])
    .filter((group) => isSameNdc(group.ndc, ndc))
    .flatMap((group) => group.lots ?? [])
    .find((candidate) =>
      isAtEditedFacility(item, editedItem)
        ? candidate.inventoryLotId === targetLot.inventoryLotId
        : isSameLotNumber(candidate.lotNumber, targetLot.lotNumber) &&
          toDateOnly(candidate.expirationDate) === toDateOnly(targetLot.expirationDate)
    ) ?? null;

/** The row's facility record of the lot, or null when the row is already on it or cannot fill. */
const usableLotForRow = ({ row, lot, todayStr, isManualLotEnabled }) => {
  const { item } = row;
  if (!lot || soleRecordedLot(item)?.inventoryLotId === lot.inventoryLotId) return null;

  const isSelectable = isLotSelectableForFill({
    lot,
    fillQuantity: Number(item.fillQuantity ?? 0),
    todayStr,
    allowShortfall: mayClaimShortfall(item, isManualLotEnabled),
  });

  return isSelectable ? lot : null;
};

/**
 * A hand-recorded lot is one vial in the operator's hand, so it reaches only the rows that fill at
 * the edited row's facility, and only those that may record a lot by hand there.
 */
const canTakeManualLot = ({
  row,
  editedItem,
  manualLot,
  ndc,
  recordableLots,
  isManualLotEnabled,
}) => {
  const { item } = row;
  if (!isAtEditedFacility(item, editedItem)) return false;

  const recordable = entryForProductAndFacility(recordableLots, item);
  const canRecordManually =
    mayClaimShortfall(item, isManualLotEnabled) &&
    !!recordable?.hasInventoryMaster &&
    isRecordableNdc({ ndc, ndcOptions: recordable?.ndcOptions });
  if (!canRecordManually) return false;

  const recorded = soleRecordedLot(item);
  const isAlreadyOnIt =
    !!recorded &&
    ((!!manualLot.inventoryLotId && recorded.inventoryLotId === manualLot.inventoryLotId) ||
      (recorded.isManualEntry === true &&
        isSameLotNumber(recorded.lotNumber, manualLot.lotNumber) &&
        toDateOnly(recorded.expirationDate) === toDateOnly(manualLot.expirationDate)));

  return !isAlreadyOnIt;
};

/** `[{ facilityName, rowCount }]`, one per facility the rows fill at, A–Z by name. */
const countRowsByFacility = (rows) => {
  const countById = new Map();
  rows.forEach(({ item }) => {
    const current = countById.get(item.fulfillingPharmacyId);
    countById.set(item.fulfillingPharmacyId, {
      facilityName: item.fulfillingPharmacyName || 'another facility',
      rowCount: (current?.rowCount ?? 0) + 1,
    });
  });

  return [...countById.values()].sort((left, right) =>
    left.facilityName.localeCompare(right.facilityName, undefined, { sensitivity: 'base' })
  );
};

/** How much of this lot the row already holds: only a row on two lots can hold part of one. */
const shareAlreadyHeld = (item, inventoryLotId) =>
  (item.recordedLots ?? [])
    .filter((recorded) => recorded.inventoryLotId === inventoryLotId)
    .reduce((sum, recorded) => sum + Number(recorded.quantity || 0), 0);

/**
 * One stock lot, shared out in priority order: the rows that need a lot take it first, then the
 * rows that are fine, each in grid order. A row the stock cannot cover is staged with its claim
 * (POM-6537 decision 1b) when it may record System Deficit and the lot may be claimed, whether it
 * needs attention or is fine: the same rule as picking a short lot for one row in Change NDC & lot.
 * Only a row that may not claim is left off.
 *
 * A row moving onto the lot gives back any share of it already held, and that share does not
 * count against it.
 */
const shareStockLot = ({ candidates, untickedIds, ndc, balanceOf, isManualLotEnabled }) => {
  // Lot id -> what it has left after the rows already given it, counting every row's held share.
  const leftAfterEarlierRows = new Map();

  return candidates.flatMap(({ row, lot }) => {
    const id = row.item.requestLineItemId;
    if (untickedIds.has(id)) return [{ row, isTicked: false }];

    const lotId = lot.inventoryLotId;
    const fillQuantity = Number(row.item.fillQuantity ?? 0);
    const heldShare = shareAlreadyHeld(row.item, lotId);
    const balance = balanceOf(lotId, id);
    const freeQty = leftAfterEarlierRows.has(lotId)
      ? leftAfterEarlierRows.get(lotId) + heldShare
      : balance.freeQty;
    const canClaimRest = mayClaimShortfall(row.item, isManualLotEnabled) && balance.canClaim;
    if (freeQty < fillQuantity && !canClaimRest) return [];

    leftAfterEarlierRows.set(lotId, freeQty - fillQuantity);
    const allocation = buildStockLotSelection({
      lot: { ...lot, remainingQty: Math.max(0, freeQty) },
      fillQuantity,
    });

    return [
      {
        row,
        isTicked: true,
        deficitQty: allocation.unverifiedQty ?? 0,
        change: { ndc, lotAllocations: [allocation], changeSource: BULK_APPLY_CHANGE_SOURCE },
      },
    ];
  });
};

/**
 * The rows one Apply would also move, for the "Also apply" section of Change NDC & lot and for
 * "Bulk lot update". The latter edits no row: its `editedItem` is only the anchor of the pick (the
 * product, NDC and facility the lot is picked at, with no requestLineItemId), so every row of the
 * NDC is a candidate.
 *
 * `classified` is classifyRows' output for the current `changes`, and `claims` the claims staged
 * on it (useBatchLotClaims'). `selection` is what Apply would stage for the edited row:
 * `{ ndc, lotAllocations, stockLot }`, or null when the row has no move for the others to follow
 * (nothing valid picked yet, or its own lot). Returns how many
 * other rows share the NDC, why the choice is not available (or null), and one entry per listed
 * row, ticked or not; a ticked entry carries the change to stage.
 *
 * Also, per facility: the ticked rows that take the lot from a facility other than the edited
 * row's (`tickedRowsByOtherFacility`), and the rows left out because their facility cannot take
 * this lot (`rowsLeftOutByFacility`): it does not stock a stock lot, or it is not where a lot
 * recorded by hand is.
 */
export const planBulkApply = ({
  classified,
  claims,
  items,
  availability,
  recordableLots,
  changes,
  todayStr,
  isManualLotEnabled,
  editedItem,
  selectedNdc,
  selection,
  scope,
  untickedIds,
}) => {
  const ndc = editedItem?.effectiveNdc ?? null;
  const isOtherRow = (row) => row.item.requestLineItemId !== editedItem?.requestLineItemId;
  const others = [...(classified?.rows ?? []), ...(classified?.unlistedRows ?? [])].filter(
    isOtherRow
  );
  const sameNdcCount = others.filter((row) => isRowOnNdc({ row, changes, ndc })).length;
  const plan = {
    otherRowCount: sameNdcCount,
    unavailableReason: null,
    entries: [],
    tickedRowsByOtherFacility: [],
    rowsLeftOutByFacility: [],
  };

  if (sameNdcCount === 0) return plan;
  if (!selection) return { ...plan, unavailableReason: BULK_APPLY_UNAVAILABLE.NO_LOT_PICKED };
  if (!isSameNdc(selectedNdc, ndc)) {
    return { ...plan, unavailableReason: BULK_APPLY_UNAVAILABLE.OTHER_NDC_PICKED };
  }

  const [targetAllocation] = selection.lotAllocations;
  const batch = batchWithPick({
    classified,
    items,
    availability,
    changes,
    claims,
    editedItem,
    selection,
    isManualLotEnabled,
  });
  // The NDC as well as the product: one product can carry several NDCs, and a row of another NDC
  // moved onto this lot would print this NDC on its label.
  const inScope = batch.rows.filter(
    (row) =>
      isOtherRow(row) &&
      row.item.productId === editedItem.productId &&
      isRowOnNdc({ row, changes, ndc }) &&
      isInScope(row, scope)
  );
  const prioritised = [
    ...inScope.filter(isInNeedsAttention),
    ...inScope.filter((row) => !isInNeedsAttention(row)),
  ];
  const unticked = new Set(untickedIds ?? []);

  if (isManualLotSelection(targetAllocation)) {
    return {
      ...plan,
      entries: prioritised
        .filter((row) =>
          canTakeManualLot({
            row,
            editedItem,
            manualLot: targetAllocation,
            ndc,
            recordableLots,
            isManualLotEnabled,
          })
        )
        .map((row) => {
          if (unticked.has(row.item.requestLineItemId)) return { row, isTicked: false };

          const quantity = Number(row.item.fillQuantity ?? 0);

          return {
            row,
            isTicked: true,
            // Recorded by hand, so the whole quantity is claimed.
            deficitQty: quantity,
            change: {
              ndc,
              lotAllocations: [buildManualLotSelection({ ...targetAllocation, quantity })],
              changeSource: BULK_APPLY_CHANGE_SOURCE,
            },
          };
        }),
      rowsLeftOutByFacility: countRowsByFacility(
        prioritised.filter((row) => !isAtEditedFacility(row.item, editedItem))
      ),
    };
  }

  const targetLot = { ...targetAllocation, expirationDate: selection.stockLot?.expirationDate };
  const lotAtFacilityByRow = prioritised.map((row) => ({
    row,
    lotAtFacility: findPickedLotAtRowFacility({
      item: row.item,
      editedItem,
      targetLot,
      ndc,
      availability,
    }),
  }));
  const candidates = lotAtFacilityByRow
    .map(({ row, lotAtFacility }) => ({
      row,
      lot: usableLotForRow({ row, lot: lotAtFacility, todayStr, isManualLotEnabled }),
    }))
    .filter(({ lot }) => !!lot);
  // The edited row's pick is counted as staged, so the rows after it share what it leaves.
  const balanceOf = lotBalancesInBatch({ items, availability, changes: batch.changes });
  const entries = shareStockLot({
    candidates,
    untickedIds: unticked,
    ndc,
    balanceOf,
    isManualLotEnabled,
  });

  return {
    ...plan,
    entries,
    tickedRowsByOtherFacility: countRowsByFacility(
      entries
        .filter((entry) => entry.isTicked && !isAtEditedFacility(entry.row.item, editedItem))
        .map((entry) => entry.row)
    ),
    rowsLeftOutByFacility: countRowsByFacility(
      lotAtFacilityByRow.filter(({ lotAtFacility }) => !lotAtFacility).map(({ row }) => row)
    ),
  };
};
